import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { X, Lock, Camera } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const profileSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Senha atual é obrigatória'),
  newPassword: z.string().min(8, 'Nova senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z.string().min(1, 'Confirmação é obrigatória'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { user, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      nome: user?.nome || '',
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    if (isOpen && user?.nome) {
      profileForm.reset({ nome: user.nome });
    }
  }, [isOpen, user?.nome, profileForm]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isPasswordDialogOpen) {
          setIsPasswordDialogOpen(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPasswordDialogOpen, onClose]);

  if (!isOpen) return null;

  const handleProfileSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    try {
      const response = await api.updateProfile(data.nome);
      if (setUser && response.data?.user) {
        setUser({ ...user!, nome: response.data.user.nome, avatarUrl: response.data.user.avatarUrl });
      }
      toast.success('Nome atualizado com sucesso!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar nome');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('O arquivo deve ser uma imagem');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Falha no upload');
      }

      const data = await response.json();

      const avatarResponse = await api.updateAvatar(data.fileUrl);
      if (setUser && avatarResponse.data?.user) {
        setUser({ ...user!, avatarUrl: avatarResponse.data.user.avatarUrl });
      }
      toast.success('Foto de perfil atualizada!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar foto');
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    setIsLoading(true);
    try {
      const response = await api.deleteAvatar();
      if (setUser && response.data?.user) {
        setUser({ ...user!, avatarUrl: undefined });
      }
      toast.success('Foto de perfil removida!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao remover foto');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (data: PasswordFormData) => {
    setIsLoading(true);
    try {
      await api.updatePassword(data.currentPassword, data.newPassword, data.confirmPassword);
      toast.success('Senha atualizada com sucesso!');
      passwordForm.reset();
      setIsPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar senha');
    } finally {
      setIsLoading(false);
    }
  };

  const initials = user?.nome
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || '?';

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
          onClick={onClose} 
        />
        
        <div className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-white/10 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
            <h2 className="text-lg font-bold text-white uppercase tracking-[0.2em] font-oswald">
              Meu Perfil
            </h2>
            <button
              onClick={onClose}
              className="p-3 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/20"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
            <div className="flex gap-6">
              <div className="flex-1 bg-white/5 rounded-[2rem] border border-white/10 p-6 space-y-5 backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] font-oswald">
                    Informações Pessoais
                  </span>
                </div>
                
                <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-4">
                  <div>
                    <label htmlFor="nome" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2 mb-2">
                      Nome
                    </label>
                    <input
                      id="nome"
                      type="text"
                      {...profileForm.register('nome')}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-sm tracking-wide"
                      placeholder="Seu nome"
                    />
                    {profileForm.formState.errors.nome && (
                      <p className="mt-2 text-xs text-red-400">{profileForm.formState.errors.nome.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2 mb-2">
                      E-mail
                    </label>
                    <div className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-4 text-gray-300 font-oswald text-sm tracking-wide">
                      {user?.email}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 border border-blue-500 font-oswald disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Salvando...' : 'Salvar Nome'}
                  </button>
                </form>
              </div>

              <div className="w-48 bg-white/5 rounded-[2rem] border border-white/10 p-6 space-y-4 backdrop-blur-md flex flex-col items-center">
                <div className="flex items-center gap-2 self-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] font-oswald">
                    Foto
                  </span>
                </div>
                
                <div className="flex flex-col items-center flex-1">
                  <div className="relative">
                    <div 
                      className="w-24 h-24 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 p-[3px] cursor-pointer group"
                      onClick={() => !isLoading && fileInputRef.current?.click()}
                    >
                      <div className="w-full h-full rounded-full bg-[#1e293b] flex items-center justify-center overflow-hidden">
                        {user?.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-bold text-2xl font-oswald">{initials}</span>
                        )}
                      </div>
                      <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera size={24} className="text-white" />
                      </div>
                      {isLoading && (
                        <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                          <svg className="animate-spin w-8 h-8 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarSelect}
                    className="hidden"
                  />

                  {user?.avatarUrl && (
                    <button
                      onClick={handleRemoveAvatar}
                      disabled={isLoading}
                      className="mt-3 text-[10px] text-red-400 hover:text-red-300 uppercase tracking-[0.2em] font-oswald font-bold disabled:opacity-50"
                    >
                      Remover foto
                    </button>
                  )}
                  
                  <p className="text-[9px] text-gray-500 text-center mt-2 font-oswald tracking-wide">
                    JPG, PNG ou WEBP<br />Máximo 5MB
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-[2rem] border border-white/10 p-6 backdrop-blur-md">
              <button
                onClick={() => setIsPasswordDialogOpen(true)}
                className="w-full px-6 py-3 bg-white/5 text-gray-300 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/10 hover:border-white/20 font-oswald flex items-center justify-center gap-2"
              >
                <Lock size={14} />
                Trocar Senha
              </button>
            </div>
          </div>
        </div>
      </div>

      {isPasswordDialogOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setIsPasswordDialogOpen(false)} 
          />
          
          <div className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/10 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-sm font-bold text-white uppercase tracking-[0.2em] font-oswald">
                Trocar Senha
              </h3>
              <button
                onClick={() => setIsPasswordDialogOpen(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all border border-white/5 hover:border-white/20"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="p-6 space-y-4">
              <div>
                <label htmlFor="currentPassword" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2 mb-2">
                  Senha Atual
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  {...passwordForm.register('currentPassword')}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-sm tracking-wide"
                  placeholder="••••••••"
                />
                {passwordForm.formState.errors.currentPassword && (
                  <p className="mt-2 text-xs text-red-400">{passwordForm.formState.errors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="newPassword" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2 mb-2">
                  Nova Senha
                </label>
                <input
                  id="newPassword"
                  type="password"
                  {...passwordForm.register('newPassword')}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-sm tracking-wide"
                  placeholder="Mínimo 8 caracteres"
                />
                {passwordForm.formState.errors.newPassword && (
                  <p className="mt-2 text-xs text-red-400">{passwordForm.formState.errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2 mb-2">
                  Confirmar Nova Senha
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  {...passwordForm.register('confirmPassword')}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-sm tracking-wide"
                  placeholder="••••••••"
                />
                {passwordForm.formState.errors.confirmPassword && (
                  <p className="mt-2 text-xs text-red-400">{passwordForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 border border-blue-500 font-oswald disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Atualizando...' : 'Atualizar Senha'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}